/* eslint-disable @typescript-eslint/no-explicit-any */

import { Fn }			from "ts-base/fn";
import { Branded }		from "ts-base/branded";
import { Predicate }	from "ts-base/predicate";
import { Validated }	from "ts-base/validated";
import { Nea }			from "ts-base/nea";
import { Equal }		from "ts-base/equal";
import { Comparator }	from "ts-base/comparator";
import { Validator }	from "ts-base/validation/validator";
import * as Q			from "ts-base/validation/query";
import * as J			from "ts-base/validation/json";
import {
	Numeric as BaseNumeric,
}	from "ts-base/numeric/numeric";

import { Rejecter }		from "@v3/data/rejecter";

export namespace Numeric {
	// TODO base a Simplify would be nice here but slows down compilation quite a bit
	export type Companion<B extends string>	= BaseNumeric<B> & Extension<B>;

	export type Value<T>	= T extends Companion<infer B> ? Branded<number, B> : never;

	export type Extension<B extends string>	= Readonly<{
		// string representation
		validateString:	Validator<string, Branded<number, B>>,
		fromString:		Fn<string, Branded<number, B>|null>,
		toString:		Fn<Branded<number, B>, string>,

		// never includes the name in error messages
		rejecter:		Rejecter<number>,

		// does include the name in error messages
		validateNumber:	Validator<number, Branded<number, B>>,

		query:			Q.ParameterSchema<Branded<number, B>>,
		json:			J.Schema<Branded<number, B>>,

		unbrand:		Fn<Branded<number, B>, number>,

		rebrand:		<BB extends string>(name:BB)											=> Companion<BB>,
		subtype:		<BB extends string>(name:BB, additionalRejecter:Rejecter<number>)		=> Companion<BB>,
		constrain:		<BB extends string>(name:BB, minimum:number|null, maximum:number|null)	=> Companion<BB>,
	}>;

	//-----------------------------------------------------------------------------

	// TODO base allow extensions for these?

	export const unrestricted	= <B extends string>(name:B):Companion<B>	=>
		generic(name, Rejecter.accept());

	export const predicate	= <B extends string>(name:B, predicate:Predicate<number>, rejectionMessage:string):Companion<B>	=>
		generic(name, Rejecter.predicate(predicate, rejectionMessage));

	export const generic	= <B extends string>(name:B, rejecter:Rejecter<number>):Companion<B>	=> {
		const brand			= Branded.brand<Branded<number, B>>;
		const is			= (it:number):it is Branded<number, B>	=> Rejecter.test(rejecter, it);
		const fromNumber	= (it:number):Branded<number, B>|null => is(it) ? brand(it) : null;

		return {
			// base

			name:			name,

			brand:			brand,
			is:				is,

			fromNumber:		fromNumber,
			toNumber:		Fn.identity<number>,

			equal:			Equal.builtinOf<Branded<number, B>>(),
			compare:		Comparator.builtinOf<Branded<number, B>>(),

			// companion

			...extension(name, brand, rejecter),
		};
	};

	// TODO base a Simplify would be nice here
	// this returns a Companion plus all functions defined only on C
	export const fromBase	= <C extends BaseNumeric<any>>(base:C, rejectionMessage:string):C & Extension<BaseNumeric.Brand<C>>	=> ({
		...base,
		...extension(base.name, base.brand, Rejecter.predicate(base.is, rejectionMessage)),
	});

	//-----------------------------------------------------------------------------

	const extension	= <B extends string>(name:string, brand:Fn<number, Branded<number, B>>, rejecter:Rejecter<number>):Extension<B>	=> {
		const validateNumber:Validator<number, Branded<number, B>> = (it) => {
			const errors	= rejecter(it);
			return (
				Nea.is(errors)
				?	Validated.invalid(Nea.map(errors)(it => `${name}: ${it}`))
				:	Validated.valid(brand(it))
			);
		};

		const validateString:Validator<string, Branded<number, B>> = (it) =>
			/^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/.test(it)
			?	validateNumber(parseFloat(it))
			:	Validated.invalid1(`${name}: must be a number`);

		const rebrand	= <BB extends string>(name:BB):Companion<BB>	=>
			generic(name, rejecter);

		const subtype	= <BB extends string>(name:BB, additionalRejecter:Rejecter<number>):Companion<BB>	=>
			generic(name, Rejecter.concat(additionalRejecter, rejecter));

		const constrain	= <BB extends string>(name:BB, minimum:number|null, maximum:number|null):Companion<BB>	=>
			subtype(name, it =>
				minimum !== null && it < minimum ? [ `value must be at least ${minimum}`	]	:
				maximum !== null && it > maximum ? [ `value must be at most ${maximum}`		]	:
				[]
			);

		return {
			validateString:	validateString,
			fromString:		(it:string):Branded<number, B>|null	=> Validated.toNullable(validateString(it)),
			toString:		(value:Branded<number, B>):string	=> value.toString(),

			rejecter:		rejecter,

			validateNumber:	validateNumber,

			query:			validateString,
			json:			J.numberThen(validateNumber),

			unbrand:		(value:Branded<number, B>):number	=> value,

			rebrand:		rebrand,
			subtype:		subtype,
			constrain:		constrain,
		};
	};
}
